import { BeastMcpRegistry } from './registry.js';
import { setRegistry } from './decorators.js';
import { ToolMetadata, ResourceMetadata } from './types.js';
import { ContextFactory } from './context.js';
import { ErrorHandler, EnhancedToolExecutionError, EnhancedResourceError } from './errors.js';
import { MetadataRegistry } from './metadata.js';

/**
 * BeastMCP Server class that integrates with the MCP SDK
 * Note: This is a simplified implementation for Phase 1
 * Full MCP SDK integration will be implemented in Phase 2
 */
export class BeastMcpServer {
  private registry: BeastMcpRegistry;
  private metadataRegistry: MetadataRegistry;
  private isInitialized = false;

  constructor() {
    this.registry = new BeastMcpRegistry();
    this.metadataRegistry = new MetadataRegistry();
    
    // Set up the registry for decorators
    setRegistry(this.registry, this.metadataRegistry);
  }

  /**
   * Initialize the server and set up handlers
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    console.log('BeastMCP Server initialized');
    this.isInitialized = true;
  }

  /**
   * Start the server
   */
  async start(): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    console.log('BeastMCP Server started');
    console.log(`Registered tools: ${this.registry.getToolCount()}`);
    console.log(`Registered resources: ${this.registry.getResourceCount()}`);
  }

  /**
   * Stop the server
   */
  async stop(): Promise<void> {
    console.log('BeastMCP Server stopped');
  }

  /**
   * Get the registry instance
   */
  getRegistry(): BeastMcpRegistry {
    return this.registry;
  }

  /**
   * Get all registered tools
   */
  getTools(): ToolMetadata[] {
    return this.registry.getAllTools();
  }

  /**
   * Get all registered resources
   */
  getResources(): ResourceMetadata[] {
    return this.registry.getAllResources();
  }

  /**
   * Get the metadata registry
   */
  getMetadataRegistry(): MetadataRegistry {
    return this.metadataRegistry;
  }

  /**
   * Export LLM-friendly schema
   */
  exportLLMSchema(): string {
    return this.metadataRegistry.exportMetadata();
  }

  /**
   * Execute a tool by name
   */
  async executeTool(name: string, args: any): Promise<any> {
    const tool = this.registry.getTool(name);
    if (!tool) {
      throw new Error(`Tool '${name}' not found`);
    }

    const startTime = Date.now();
    const errorHandler = ErrorHandler.getInstance();
    
    try {
      const context = ContextFactory.createWithUniqueSession(name);
      const result = await tool.handler(args, context);
      return result;
    } catch (error) {
      const executionTime = Date.now() - startTime;
      const enhancedError = new EnhancedToolExecutionError(
        error instanceof Error ? error.message : String(error),
        name,
        undefined,
        executionTime
      );
      errorHandler.handleError(enhancedError);
      throw enhancedError;
    }
  }

  /**
   * Read a resource by name
   */
  async readResource(name: string): Promise<any> {
    const resource = this.registry.getResource(name);
    if (!resource) {
      throw new Error(`Resource '${name}' not found`);
    }

    const errorHandler = ErrorHandler.getInstance();
    
    try {
      const context = ContextFactory.createWithUniqueSession(name);
      const result = await resource.handler(context);
      return result;
    } catch (error) {
      const enhancedError = new EnhancedResourceError(
        error instanceof Error ? error.message : String(error),
        name
      );
      errorHandler.handleError(enhancedError);
      throw enhancedError;
    }
  }
} 